import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Switch,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { useFilter } from '../contexts/FilterContext';
import { tenantService } from '../services/tenantService';
import { serverService } from '../services/serverService';
import type { Tenant } from '../types/tenant';
import type { Server } from '../types/server';

interface TopFilterBarProps {
  onRefresh?: () => void;
  autoRefresh?: boolean;
  onAutoRefreshChange?: (enabled: boolean) => void;
}

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const TopFilterBar = ({ onRefresh, autoRefresh = false, onAutoRefreshChange }: TopFilterBarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    selectedTenantIds,
    selectedServerIds,
    setSelectedServerIds,
    toggleTenant,
    toggleServer,
    selectAllTenants,
    selectAllServers,
    clearTenantSelection,
    clearServerSelection,
    isTenantSelected,
    isServerSelected,
    isRefreshing,
  } = useFilter();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [tenantMenuAnchor, setTenantMenuAnchor] = useState<null | HTMLElement>(null);
  const [serverMenuAnchor, setServerMenuAnchor] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [tenantsData, serversData] = await Promise.all([
        tenantService.getAll(),
        serverService.getAll(),
      ]);
      setTenants(tenantsData);
      setServers(serversData);
    } catch (error) {
      console.error('Veriler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTenantMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTenantMenuAnchor(event.currentTarget);
  };

  const handleTenantMenuClose = () => {
    setTenantMenuAnchor(null);
  };

  const handleServerMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setServerMenuAnchor(event.currentTarget);
  };

  const handleServerMenuClose = () => {
    setServerMenuAnchor(null);
  };

  const handleSelectAllTenants = () => {
    if (selectedTenantIds.length === tenants.length) {
      clearTenantSelection();
    } else {
      selectAllTenants(tenants.map((t) => t.id));
    }
  };

  const activeServers = servers.filter((s) => s.isActive);
  const filteredServers = activeServers.filter(
    (s) => selectedTenantIds.length === 0 || selectedTenantIds.includes(s.tenantId)
  );

  const handleSelectAllServers = () => {
    if (selectedServerIds.length === filteredServers.length && filteredServers.length > 0) {
      clearServerSelection();
    } else {
      selectAllServers(filteredServers.map((s) => s.id));
    }
  };

  const selectedTenantCount = selectedTenantIds.length;
  const selectedServerCount = selectedServerIds.length;
  const totalTenantCount = tenants.length;
  const totalServerCount = filteredServers.length;

  const getTenantButtonText = () => {
    if (selectedTenantCount === 0) return 'Şirket Grubu Seç';
    if (selectedTenantCount === totalTenantCount) return 'Tüm Şirket Grupları';
    return `${selectedTenantCount} şirket grubu seçili`;
  };

  const getServerButtonText = () => {
    if (selectedServerCount === 0) return 'Server Seç';
    if (selectedServerCount === totalServerCount && totalServerCount > 0) return 'Tüm Server\'lar';
    return `${selectedServerCount} Server seçili`;
  };

  const tenantButton = (
    <Tooltip title={getTenantButtonText()}>
      <span>
        <IconButton
          size="small"
          onClick={handleTenantMenuOpen}
          disabled={loading}
          sx={{
            color: 'inherit',
            border: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.8)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
          aria-label={getTenantButtonText()}
        >
          <Badge badgeContent={selectedTenantCount > 0 ? selectedTenantCount : undefined} color="primary" max={99}>
            <BusinessIcon fontSize="small" />
          </Badge>
        </IconButton>
      </span>
    </Tooltip>
  );

  const serverButton = (
    <Tooltip title={getServerButtonText()}>
      <span>
        <IconButton
          size="small"
          onClick={handleServerMenuOpen}
          disabled={loading}
          sx={{
            color: 'inherit',
            border: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.8)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
          aria-label={getServerButtonText()}
        >
          <Badge badgeContent={selectedServerCount > 0 ? selectedServerCount : undefined} color="primary" max={99}>
            <ComputerIcon fontSize="small" />
          </Badge>
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box display="flex" alignItems="center" gap={0.5} flexWrap="nowrap">
      {isMobile ? (
        <>
          {tenantButton}
          {serverButton}
        </>
      ) : (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={handleTenantMenuOpen}
            disabled={loading}
            sx={{
              minWidth: 120,
              color: 'inherit',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {getTenantButtonText()}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={handleServerMenuOpen}
            disabled={loading}
            sx={{
              minWidth: 120,
              color: 'inherit',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {getServerButtonText()}
          </Button>
        </>
      )}

        <Menu
          anchorEl={tenantMenuAnchor}
          open={Boolean(tenantMenuAnchor)}
          onClose={handleTenantMenuClose}
          PaperProps={{
            sx: { maxHeight: 400, width: 300, maxWidth: 'calc(100vw - 24px)', overflow: 'hidden' },
          }}
          MenuListProps={{
            sx: { maxHeight: 400, overflowY: 'auto', py: 0 },
          }}
        >
          <Box sx={{ p: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTenantCount === totalTenantCount && totalTenantCount > 0}
                  indeterminate={selectedTenantCount > 0 && selectedTenantCount < totalTenantCount}
                  onChange={handleSelectAllTenants}
                />
              }
              label={
                <Typography variant="body2" fontWeight="bold">
                  Tümünü Seç ({totalTenantCount})
                </Typography>
              }
            />
            <Divider sx={{ my: 1 }} />
            {tenants.map((tenant) => (
              <MenuItem
                key={tenant.id}
                onClick={() => {
                  toggleTenant(tenant.id);
                  if (selectedTenantIds.includes(tenant.id)) {
                    // Tenant seçimi kaldırıldığında, o tenant'a ait server seçimlerini de kaldır
                    const serversToRemove = servers
                      .filter((s) => s.tenantId === tenant.id)
                      .map((s) => s.id);
                    setSelectedServerIds(selectedServerIds.filter((id) => !serversToRemove.includes(id)));
                  }
                }}
                dense
              >
                <Checkbox checked={isTenantSelected(tenant.id)} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {tenant.name}
                </Typography>
              </MenuItem>
            ))}
          </Box>
        </Menu>

        <Menu
          anchorEl={serverMenuAnchor}
          open={Boolean(serverMenuAnchor)}
          onClose={handleServerMenuClose}
          PaperProps={{
            sx: { maxHeight: 400, width: 300, maxWidth: 'calc(100vw - 24px)', overflow: 'hidden' },
          }}
          MenuListProps={{
            sx: { maxHeight: 400, overflowY: 'auto', py: 0 },
          }}
        >
          <Box sx={{ p: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedServerCount === totalServerCount && totalServerCount > 0}
                  indeterminate={selectedServerCount > 0 && selectedServerCount < totalServerCount}
                  onChange={handleSelectAllServers}
                />
              }
              label={
                <Typography variant="body2" fontWeight="bold">
                  Tümünü Seç ({totalServerCount})
                </Typography>
              }
            />
            <Divider sx={{ my: 1 }} />
            {filteredServers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                {selectedTenantIds.length === 0
                  ? 'Önce şirket grubu seçin'
                  : 'Seçili şirket gruplarında sunucu bulunamadı'}
              </Typography>
            ) : (
              filteredServers.map((server) => (
                <MenuItem
                  key={server.id}
                  onClick={() => toggleServer(server.id)}
                  dense
                >
                  <Checkbox checked={isServerSelected(server.id)} />
                  <Box sx={{ ml: 1, flex: 1 }}>
                    <Typography variant="body2">{server.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tenants.find((t) => t.id === server.tenantId)?.name || ''}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Box>
        </Menu>

      {onRefresh && (
        <Tooltip title="Yenile">
          <IconButton
            onClick={onRefresh}
            size="small"
            disabled={isRefreshing}
            sx={{
              color: 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              '& .MuiSvgIcon-root': {
                animation: isRefreshing ? `${rotate} 1s linear infinite` : 'none',
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      )}

      {onAutoRefreshChange && (
        <Tooltip title="Otomatik yenile (60sn)">
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => onAutoRefreshChange(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'white',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              />
            }
            label=""
            sx={{
              ml: 1,
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default TopFilterBar;

